import { toProblem } from '@rental/contracts/client'
import type { StaffProblem } from './problems'
import { isNativeShell } from './shell'

/**
 * A1: the files the staff app shows from the API (W1, W2, CUS-2), and the
 * only place their addresses are written (a unit test keeps it so).
 * - The web app opens them from links and images: the browser sends its
 *   cookies, and the server refuses a load from any other site.
 * - The iPad app cannot: it fetches each one with its credentials and shows it
 *   from memory, as a blob of a type from the list below and nothing else. A
 *   blob runs in the app's own origin, so never a page that could run.
 */
export type StaffFile =
  /** A vehicle photo (OPS-1, OPS-2): a thumbnail, fetched as it is shown. */
  | { kind: 'photo'; mediaId: string }
  /** An identity document (CUS-2): fetched only when someone opens it, and each view logged. */
  | { kind: 'document'; mediaId: string }
  /** The signed contract (CTR-5). */
  | { kind: 'contract'; code: string }
  /** S6 (CMP-2): the letter naming a fine's renter to the authority; each view logged. */
  | { kind: 'fineLetter'; fineId: string }
  /** S10: a printed fallback kit, blank forms for renting on paper. */
  | { kind: 'fallbackKit'; kitId: string }
  /**
   * S5c (RPT-4): a money report as CSV for Italian Excel. The web app saves it
   * from a link; the iPad app has nowhere to put it yet, so it says PENDING.
   */
  | { kind: 'report'; report: 'revenue'; from: string; to: string; by: 'day' | 'month' }
  | { kind: 'report'; report: 'open' }
  /**
   * S8: a customer's data export, opened with the ticket the Owner's code
   * gave (this session's, for a minute). The web app saves it from a link;
   * the iPad app has nowhere to put it yet, so it says PENDING.
   */
  | { kind: 'export'; customerId: string; ticket: string }

/** The file's path on the API; the web app's link, from its own origin. */
export function filePath(file: StaffFile): string {
  switch (file.kind) {
    case 'contract':
      return `/v1/staff/bookings/${encodeURIComponent(file.code)}/contract.pdf`
    case 'fineLetter':
      return `/v1/staff/fines/${encodeURIComponent(file.fineId)}/letter.pdf`
    case 'fallbackKit':
      return `/v1/staff/fallback-kits/${encodeURIComponent(file.kitId)}/kit.pdf`
    case 'report':
      return file.report === 'open'
        ? '/v1/staff/reports/open.csv'
        : `/v1/staff/reports/revenue.csv?${new URLSearchParams({ from: file.from, to: file.to, by: file.by })}`
    case 'export':
      return `/v1/staff/customers/${encodeURIComponent(file.customerId)}/export.zip?${new URLSearchParams({ t: file.ticket })}`
    default:
      return `/v1/staff/media/${encodeURIComponent(file.mediaId)}`
  }
}

/** What each kind may be: the photos and PDFs the API stores, the reports' CSV, an export's ZIP. */
const ALLOWED: Readonly<Record<StaffFile['kind'], readonly string[]>> = {
  photo: ['image/jpeg', 'image/png'],
  document: ['image/jpeg', 'image/png', 'application/pdf'],
  contract: ['application/pdf'],
  fineLetter: ['application/pdf'],
  fallbackKit: ['application/pdf'],
  report: ['text/csv'],
  export: ['application/zip'],
}

/** The type the app will give the file's blob, or null when it shows no such file. */
export function allowedType(kind: StaffFile['kind'], header: string | null): string | null {
  const type = header?.split(';')[0]?.trim().toLowerCase()
  return type && ALLOWED[kind].includes(type) ? type : null
}

export type FileResult = { ok: true; blob: Blob } | { ok: false; problem: StaffProblem }

/** The iPad app: the file, fetched with its credentials (through `fetch`, the keeper's). */
export async function fetchFile(
  file: StaffFile,
  options: {
    origin: string
    appVersion: string
    fetch: (request: Request) => Promise<Response>
    signal?: AbortSignal
  },
): Promise<FileResult> {
  const request = new Request(`${options.origin}${filePath(file)}`, {
    headers: { 'x-app-version': options.appVersion },
    credentials: 'omit',
    ...(options.signal ? { signal: options.signal } : {}),
  })
  try {
    const response = await options.fetch(request)
    if (!response.ok) {
      return {
        ok: false,
        problem: toProblem(response.status, await response.json().catch(() => undefined)),
      }
    }
    const type = allowedType(file.kind, response.headers.get('content-type'))
    if (!type) {
      await response.body?.cancel()
      return { ok: false, problem: { kind: 'client', code: 'file_type_refused' } }
    }
    // Typed from the list, never from the answer.
    return { ok: true, blob: new Blob([await response.arrayBuffer()], { type }) }
  } catch {
    return { ok: false, problem: { kind: 'network' } }
  }
}

/**
 * Hands a PDF to something outside the app's pages: in a browser, a
 * download; in the iPad app, the system's viewer (A2: Quick Look, from a file
 * the app writes first). `open` answers false when it could not; `forget`
 * removes whatever `open` left on the device.
 */
export interface FileOpener {
  open(blob: Blob, name: string): Promise<boolean>
  forget(): Promise<void>
}

/**
 * A browser saves the PDF; it never opens a window on it. A page opened on a
 * blob runs in the app's origin and inherits its CSP, under which Chromium's
 * PDF viewer breaks (A1c). Freed after a minute; nothing stays to forget.
 */
export function browserOpener(scope: Window = window): FileOpener {
  return {
    async open(blob, name) {
      const url = URL.createObjectURL(blob)
      const link = scope.document.createElement('a')
      link.href = url
      link.download = name
      link.click()
      scope.setTimeout(() => URL.revokeObjectURL(url), 60_000)
      return true
    },
    async forget() {},
  }
}

/**
 * The web app saves a file the API names for download (an export's ZIP) from
 * its address: the browser streams it to disk with the cookies it sends, and
 * never holds the whole file in the page.
 */
export function saveFromLink(path: string, scope: Window = window): void {
  const link = scope.document.createElement('a')
  link.href = path
  link.download = ''
  link.click()
}

let registered: FileOpener | null | undefined

/**
 * The opener for PDFs: the browser's, or, inside the native shell, the one
 * the shell registered as it started (native/install.ts). With none there,
 * the screens say so instead of offering a button that does nothing.
 */
export function fileOpener(): FileOpener | null {
  if (registered !== undefined) return registered
  return isNativeShell() ? null : browserOpener()
}

/** A2: the native shell's viewer; undefined goes back to the default. */
export function setFileOpener(next: FileOpener | null | undefined): void {
  registered = next
}

/**
 * The screen is leaving a person (a lock, the turn to the customer, a
 * revoke): whatever the opener left on the device goes, as the cache does.
 * Best effort; the next start sweeps again.
 */
export async function forgetOpenedFiles(): Promise<void> {
  await fileOpener()
    ?.forget()
    .catch(() => {})
}

/** What one press on a file gave the screen. */
export type Pressed =
  | { kind: 'image'; blob: Blob }
  | { kind: 'opened' }
  | { kind: 'problem'; problem: StaffProblem }

/**
 * One press, one fetch: for an identity document, exactly one logged view.
 * The press stays taken until the opener has answered, not only the fetch: in
 * the iPad app a PDF is still being written for the viewer after it arrived,
 * and a second press then would fetch it, and log it, again. A press while one
 * is running does nothing (null). `busy` follows the press for the button.
 */
export function createPress(options: {
  fetch: (file: StaffFile) => Promise<FileResult>
  opener: () => FileOpener | null
  busy: (busy: boolean) => void
}): (file: StaffFile, name: string) => Promise<Pressed | null> {
  let running = false
  return async (file, name) => {
    if (running) return null
    running = true
    options.busy(true)
    try {
      const result = await options.fetch(file)
      if (!result.ok) return { kind: 'problem', problem: result.problem }
      if (result.blob.type !== 'application/pdf') return { kind: 'image', blob: result.blob }
      const opened = await options
        .opener()
        ?.open(result.blob, name)
        .catch(() => false)
      return opened
        ? { kind: 'opened' }
        : { kind: 'problem', problem: { kind: 'client', code: 'file_not_opened' } }
    } finally {
      running = false
      options.busy(false)
    }
  }
}
